import React, { useRef, useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { useParams } from 'react-router-dom';
import {
  Form,
  Row,
  Col,
  Button,
  Alert,
  Card,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaWhatsapp } from 'react-icons/fa';
import api from '../../../services/api';
import history from '../../../services/history';
import generateDopplerPDF from './generateDopplerPDF';
import CustomFileInput from '../../../components/CustomFileInput';
import useUploadFiles from '../../../hooks/useUploadFiles';
import ModalEco from './ModalEco';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import selectEcoById from '../../../services/ecodoppler';
import styles from '../../../styles.module.css';
import generateDopplerTextReport from './generateDopplerTextReport';
import mapEcoFormFields from './mapEcoFormFields';

// Função para normalizar campos float
function normalizeFloatFields(data, floatFields) {
  const result = { ...data };
  floatFields.forEach(field => {
    if (typeof result[field] === 'string') {
      const clean = result[field].replace(',', '.');
      result[field] = clean === '' ? null : parseFloat(clean);
    }
  });
  return result;
}

const floatFields = [
  'peso',
  'altura',
  'superficiecorporea',
  'septo',
  'ppve',
  'dd',
  'ds',
  'fraejeBiplanar',
  'fe3d',
  'tempoEjecaoVE',
  'tempoAEMitral',
  'indiceCardiaco',
  'diamAE',
  'indiceVolAE',
  'diamAO',
  'diamSaidaVE',
  'diamAnelPulmonar',
  'areaMitral',
  'gradienteMitral',
  'pisaMitral',
  'volumeRegMitral',
  'areaAortica',
  'pisaAortica',
  'gmvao',
  'gmvsve',
  'gmvTric',
  'gpit',
  'gradientePulm',
  'gradMitral',
  'gradAortica',
  'gradTricuspide',
  'gradPulmonar',
];

const DeleteButton = ({ field, onDelete }) => (
  <Button
    variant="outline-danger"
    size="sm"
    style={{ marginLeft: 4, padding: '0 6px', height: 30 }}
    onClick={() => onDelete(field)}
    tabIndex={-1}
  >
    ×
  </Button>
);

// as linhas comentadas significam que estamos pulando a sequencia
// nao excluir dessa sequencia
const ecoSequence = [
  // 'tipoLaudo',
  'sexo',
  'idade',
  'peso',
  'altura',
  'superficiecorporea',
  'pressaosistolica',
  'frequenciacardiaca',
  'ecocardiograma',
  'septo',
  'ppve',
  'dd',
  'ds',
  'fraejeBiplanar',
  // 'fe3d',
  'achadosDescritivos',
  'contracaoSegmentar',
  'regiaoVE',
  // 'tempoEjecaoVE',
  // 'tempoAEMitral',
  // 'indiceCardiaco',
  'comentariosDiast',
  'diamAE',
  'indiceVolAE',
  'atrioEsq',
  'valvaMitral',
  'diamAO',
  // 'diamSaidaVE',
  'valvaAortica',
  'aorta',
  'atrioDireito',
  'veiaCavaInferior',
  'ventriculoDireito',
  'valvaTricuspide',
  'valvaPulmonar',
  // 'diamAnelPulmonar',
  'pericardio',
  'areaMitral',
  'gradienteMitral',
  'refluxoMitral',
  'pisaMitral',
  'volumeRegMitral',
  'areaAortica',
  'pisaAortica',
  'refluxoAortica',
  'gmvao',
  'gmvsve',
  'gmvTric',
  'gpit',
  'refluxoTric',
  'gradientePulm',
  'refluxoPulm',
  'gradMitral',
  'refluxoProtMitral',
  'intensidadeProtMitral',
  'gradAortica',
  'refluxoProtAortica',
  'intensidadeProtAortica',
  'gradTricuspide',
  'refluxoProtTricuspide',
  'intensidadeProtTricuspide',
  'gradPulmonar',
  'refluxoProtPulmonar',
  'intensidadeProtPulmonar',
  'valvulopatia',
  'intensidadeValv',
  'predominanciaValv',
  'protese',
  'descProtese',
  'predominanciaProtese',
  'conSeg',
  'regiao',
  'conclusoes',
  'intensidadeConclusoes',
  'observacoes',
];

const editStyle = {
  display: 'block',
  fontSize: '1.0rem',
  backgroundColor: '#d9fbda',
  border: '2px solid #016722',
  borderRadius: '5px',
  color: '#000000',
  fontWeight: 500,
  fontFamily: 'Segoe UI, sans-serif',
  marginBottom: '2px',
  padding: '2px 6px',
};

const arrayStyle = {
  width: '50px',
  fontSize: '1.1rem',
  fontWeight: 800,
  fontFamily: 'Segoe UI, sans-serif',
  display: 'block',
  backgroundColor: '#e0f2fe',
  border: '1px solid #60a5fa',
  borderRadius: '5px',
  color: '#1e3a8a',
  marginBottom: '2px',
  padding: '2px 6px',
};

export default function FormEco() {
  const { id } = useParams();
  const { data: eco, error, isLoading } = useSWR(id ? ['eco', id] : null, () =>
    selectEcoById(id)
  );
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    files,
    setFiles,
    showUpload,
    setShowUpload,
    isUploading,
    confirmUpload,
    handleCloseModal,
  } = useUploadFiles({ atividade: eco });
  const peso = parseFloat(eco?.PESOPAC) || 0;
  const altura = parseFloat(eco?.ALTURAPAC) || 0;
  const superficieCorporea =
    peso && altura
      ? (0.007184 * (altura * 100) ** 0.725 * peso ** 0.425).toFixed(2)
      : '';
  const [formData, setFormData] = useState({
    codEco: eco?.CODECO || '',
    tipoLaudo: eco?.TIPOLAUDO || 'Alterado', // Normal/Alterado
    tipoEco: eco?.TIPOECO || '',
    sexo: eco?.Sexo || '',
    idade: eco?.idadePAC || '',
    peso: '',
    altura: '',
    superficiecorporea: '',
    pressaosistolica: '',
    frequenciacardiaca: '',
    // I - Ecocardiograma
    ecocardiograma: Array(10).fill(''),
    // II - Diâmetros e Função Sistólica do VE
    septo: '',
    ppve: '',
    dd: '',
    ds: '',
    fraejeBiplanar: '',
    fe3d: '',
    achadosDescritivos: Array(10).fill(''),
    contracaoSegmentar: Array(10).fill(''),
    regiaoVE: Array(10).fill(''),
    // III - Avaliação da Função Diastólica do VE
    tempoEjecaoVE: '',
    tempoAEMitral: '',
    indiceCardiaco: '',
    comentariosDiast: Array(10).fill(''),
    // IV - Átrio Esquerdo e Valva Mitral
    diamAE: '',
    indiceVolAE: '',
    atrioEsq: Array(5).fill(''),
    valvaMitral: Array(5).fill(''),
    // V - Valva Aórtica e Aorta
    diamAO: '',
    diamSaidaVE: '',
    valvaAortica: Array(5).fill(''),
    aorta: Array(5).fill(''),
    // VI - Cavidades Direita e Valvas
    atrioDireito: Array(5).fill(''),
    veiaCavaInferior: Array(5).fill(''),
    ventriculoDireito: Array(5).fill(''),
    valvaTricuspide: Array(5).fill(''),
    valvaPulmonar: Array(5).fill(''),
    diamAnelPulmonar: '',
    // VII - Pericárdio
    pericardio: Array(10).fill(''),
    // VIII - Valvulopatia da Valva Mitral
    areaMitral: '',
    gradienteMitral: '',
    refluxoMitral: Array(5).fill(''),
    pisaMitral: '',
    volumeRegMitral: '',
    // IX - Valvulopatia da Valva Aórtica
    areaAortica: '',
    pisaAortica: '',
    gmvao: '',
    gmvsve: '',
    refluxoAortica: Array(5).fill(''),
    // X - Valvulopatia de Valva Tricúspide/Pulmonar
    gmvTric: '',
    gpit: '',
    refluxoTric: Array(5).fill(''),
    refluxoPulm: Array(5).fill(''),
    gradientePulm: '',
    // XI - Próteses
    gradMitral: '',
    refluxoProtMitral: Array(5).fill(''),
    intensidadeProtMitral: Array(5).fill(''),
    gradAortica: '',
    refluxoProtAortica: Array(5).fill(''),
    intensidadeProtAortica: Array(5).fill(''),
    gradTricuspide: '',
    refluxoProtTricuspide: Array(5).fill(''),
    intensidadeProtTricuspide: Array(5).fill(''),
    gradPulmonar: '',
    refluxoProtPulmonar: Array(5).fill(''),
    intensidadeProtPulmonar: Array(5).fill(''),
    // XII - Conclusões
    valvulopatia: Array(5).fill(''),
    intensidadeValv: Array(5).fill(''),
    predominanciaValv: Array(5).fill(''),
    protese: Array(5).fill(''),
    descProtese: Array(5).fill(''),
    predominanciaProtese: Array(5).fill(''),
    conSeg: Array(5).fill(''),
    regiao: Array(5).fill(''),
    conclusoes: Array(10).fill(''),
    intensidadeConclusoes: Array(10).fill(''),
    // XIII - Observações
    observacoes: '',
  });

  // Carrega os dados do laudo aberto. A cópia de laudo anterior reusa
  // mapEcoFormFields, mas mantém codEco e idade deste exame.
  useEffect(() => {
    if (eco) {
      setFormData(prev => ({
        ...prev,
        ...mapEcoFormFields(eco),
        codEco: eco.CODECO || '',
        idade: eco.idadePAC || '',
        superficiecorporea: superficieCorporea || eco?.SUPCORP,
      }));
    }
  }, [eco]);

  const ecoArrayRefs = useRef({});
  const ecoRefs = useRef({});

  useEffect(() => {
    Object.keys(formData).forEach(key => {
      if (Array.isArray(formData[key])) {
        if (!ecoArrayRefs.current[key]) {
          ecoArrayRefs.current[key] = [];
        }
        formData[key].forEach((_, i) => {
          if (!ecoArrayRefs.current[key][i]) {
            ecoArrayRefs.current[key][i] = React.createRef();
          }
        });
      } else {
        if (!ecoRefs.current[key]) {
          ecoRefs.current[key] = React.createRef();
        }
      }
    });
  }, [formData]);

  const handleEcoKeyDown = (e, key) => {
    if (e.key === 'Enter') {
      const currentIndex = ecoSequence.indexOf(key);
      const nextKey = ecoSequence[currentIndex + 1];

      if (nextKey) {
        const nextRef = Array.isArray(formData[nextKey])
          ? ecoArrayRefs.current[nextKey]?.[0]
          : ecoRefs.current[nextKey];

        if (nextRef?.current) {
          nextRef.current.focus();
          e.preventDefault();
        }
      }
    }
  };

  const handleEcoArrayKeyDown = (e, key, index) => {
    if (e.key === 'ArrowRight' && index < formData[key].length - 1) {
      ecoArrayRefs.current[key][index + 1]?.current?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      ecoArrayRefs.current[key][index - 1]?.current?.focus();
    } else if (e.key === 'Enter' || e.key === 'ArrowDown') {
      const currentGroupIndex = ecoSequence.indexOf(key);
      const nextKey = ecoSequence[currentGroupIndex + 1];

      const nextRef = Array.isArray(formData[nextKey])
        ? ecoArrayRefs.current[nextKey]?.[0]
        : ecoRefs.current[nextKey];

      if (nextRef?.current) {
        nextRef.current.focus();
        e.preventDefault();
      }
    }
  };

  const handleArrayChange = (e, field, index) => {
    const { value } = e.target;
    // Permite vazio ou apenas números
    if (value === '' || /^\d+$/.test(value)) {
      setFormData(prev => {
        const newArray = [...prev[field]];
        newArray[index] = value;
        return {
          ...prev,
          [field]: newArray,
        };
      });
    } else {
      toast.info('Digite apenas números neste campo.');
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClearArrayAndDelete = tipo => {
    const { codEco } = formData;

    setFormData(prev => ({
      ...prev,
      [tipo]: Array(prev[tipo].length).fill(''),
    }));

    if (codEco) {
      mutate(['eco', codEco], undefined, { revalidate: true });
    }
    toast.info(
      'Atenção: é necessário gravar o laudo para salvar as alterações feitas nesta tabela.'
    );
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const normalizedData = normalizeFloatFields(formData, floatFields);
      await api.put('/eco-details', normalizedData);
      toast.success('Dados salvos com sucesso!');
      if (id) {
        await mutate(['eco', id]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao salvar dados');
    } finally {
      setIsSubmitting(false);
      scrollTo(0, 0);
    }
  };

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo de eco!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  // TODO: fazer componentes de todos esses inputs repetitivos! ou hookform?
  return (
    <>
      <Card className="mt-2">
        <LaudoCardHeader title={`ECOCARDIOGRAMA DOPPLER - ${eco?.CODECO || ''}`} />
        <div
          className="d-flex justify-content-between align-items-center w-100 p-2"
          style={{ gap: '16px' }}
        >
          {/* Grupo do Tipo Eco (Alinhado à esquerda/centro) */}
          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
            <Form.Label
              className="mb-0 font-weight-bold"
              style={{ whiteSpace: 'nowrap' }}
            >
              Tipo Eco:
            </Form.Label>

            <Form.Check
              inline
              type="radio"
              id="tipoEco-doppler"
              name="tipoEco"
              label="Doppler"
              value="EcoD"
              checked={formData.tipoEco === 'EcoD'}
              onChange={handleChange}
              size="sm"
              className="mb-0"
              ref={ecoRefs.current['tipoEco']}
              onKeyDown={e => handleEcoKeyDown(e, 'tipoEco')}
            />

            <Form.Check
              inline
              type="radio"
              id="tipoEco-congenito"
              name="tipoEco"
              label="Congênito"
              value="EcoC"
              checked={formData.tipoEco === 'EcoC'}
              onChange={handleChange}
              size="sm"
              className="mb-0"
              ref={ecoRefs.current['tipoEco']}
              onKeyDown={e => handleEcoKeyDown(e, 'tipoEco')}
            />
          </div>

          {/* Botão de Copiar (Alinhado à direita) */}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowModal(true)}
            className="d-flex align-items-center"
            style={{ whiteSpace: 'nowrap' }}
          >
            <svg
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-2"
            >
              <path d="M10 1.5A1.5 1.5 0 0 1 11.5 3v1h-7V3A1.5 1.5 0 0 1 6 1.5h4zm1.5 2A.5.5 0 0 0 11 3V1.5A2.5 2.5 0 0 0 8.5-1h-4A2.5 2.5 0 0 0 2 1.5V3a.5.5 0 0 0-.5.5v10A2.5 2.5 0 0 0 4 16h8a2.5 2.5 0 0 0 2.5-2.5v-10A.5.5 0 0 0 14 3V1.5A1.5 1.5 0 0 0 12.5 0h-4A1.5 1.5 0 0 0 7 1.5V3a.5.5 0 0 0-.5.5v10A1.5 1.5 0 0 0 8 15h4a1.5 1.5 0 0 0 1.5-1.5v-10z" />
            </svg>
            Copiar Laudo Anterior
          </Button>
        </div>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                Nome
                <Form.Control name="nome" value={eco?.NOMEPAC || ''} readOnly />
              </Col>
              <Col>
                Data de Nascimento
                <Form.Control
                  name="dataNascimento"
                  value={eco?.dataNasc || ''}
                  readOnly
                />
              </Col>
              <Col>
                Solicitante
                <Form.Control
                  name="nomeSolicitante"
                  value={eco?.Solicitou || ''}
                  readOnly
                />
              </Col>
              <Col>
                Convênio
                <Form.Control
                  name="siglaConvenio"
                  value={eco?.SIGLACONV || ''}
                  readOnly
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={2}>
                    Sexo:
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="sexo"
                      value={formData.sexo}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['sexo']}
                      onKeyDown={e => handleEcoKeyDown(e, 'sexo')}
                      readOnly
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={2}>
                    Idade:
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="idade"
                      value={formData.idade}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['idade']}
                      onKeyDown={e => handleEcoKeyDown(e, 'idade')}
                      readOnly
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={2}>
                    Peso (Kg):
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="peso"
                      value={formData.peso}
                      onChange={handleChange}
                      type="number"
                      size="sm"
                      ref={ecoRefs.current['peso']}
                      onKeyDown={e => handleEcoKeyDown(e, 'peso')}
                      style={editStyle}
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={2}>
                    Altura (m):
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="altura"
                      style={editStyle}
                      value={formData.altura}
                      onChange={handleChange}
                      type="number"
                      size="sm"
                      ref={ecoRefs.current['altura']}
                      onKeyDown={e => handleEcoKeyDown(e, 'altura')}
                    />
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={4}>
                    Superfície Corpórea (m2):
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="superficiecorporea"
                      style={editStyle}
                      value={formData.superficiecorporea}
                      onChange={handleChange}
                      type="number"
                      size="sm"
                      ref={ecoRefs.current['superficiecorporea']}
                      onKeyDown={e => handleEcoKeyDown(e, 'superficiecorporea')}
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={4}>
                    Pressão Sistólica (mmHg):
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="pressaosistolica"
                      style={editStyle}
                      value={formData.pressaosistolica}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['pressaosistolica']}
                      onKeyDown={e => handleEcoKeyDown(e, 'pressaosistolica')}
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={4}>
                    Frequência Cardíaca:
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="frequenciacardiaca"
                      style={editStyle}
                      value={formData.frequenciacardiaca}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['frequenciacardiaca']}
                      onKeyDown={e => handleEcoKeyDown(e, 'frequenciacardiaca')}
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={4}>
                    Tipo Laudo:
                  </Form.Label>
                  <Col sm={2}>
                    <Form.Check
                      name="tipoLaudo"
                      className={`m-2`}
                      type="radio"
                      label="Normal"
                      value="Normal"
                      checked={formData.tipoLaudo === 'Normal'}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['tipoLaudo']}
                      onKeyDown={e => handleEcoKeyDown(e, 'tipoLaudo')}
                    />
                  </Col>

                  <Col sm={2}>
                    <Form.Check
                      name="tipoLaudo"
                      className="m-2"
                      type="radio"
                      label="Alterado"
                      value="Alterado"
                      checked={formData.tipoLaudo === 'Alterado'}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['tipoLaudo']}
                      onKeyDown={e => handleEcoKeyDown(e, 'tipoLaudo')}
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <h5>I - Ecocardiograma</h5>
            <Row className="align-items-end mb-3">
              <Col xs="auto">
                <Form.Label>Ecocardiograma</Form.Label>
                <div className="d-flex flex-wrap gap-1">
                  {formData.ecocardiograma.map((val, i) => (
                    <Form.Control
                      // ref={ecoArrayRefs.current['valvaMitral']?.[index]}
                      key={i}
                      ref={ecoArrayRefs.current.ecocardiograma?.[i]}
                      size="sm"
                      style={arrayStyle}
                      value={val ?? ''}
                      onChange={e => handleArrayChange(e, 'ecocardiograma', i)}
                      onKeyDown={e =>
                        handleEcoArrayKeyDown(e, 'ecocardiograma', i)
                      }
                    />
                  ))}
                  <DeleteButton
                    field="ecocardiograma"
                    onDelete={handleClearArrayAndDelete}
                  />
                </div>{' '}
                {eco?.obs?.map(o => (
                  <span key={o.CODOBS} className={styles.spanStyle}>
                    [ {o.CODOBS} — {o.DESCRICAO || 'Descrição não encontrada'} ]
                  </span>
                ))}
              </Col>
            </Row>

            <h5>II - Diâmetros e Função Sistólica do VE</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Septo</Form.Label>
                  <Form.Control
                    name="septo"
                    style={editStyle}
                    type="number"
                    size="sm"
                    ref={ecoRefs.current['septo']}
                    value={formData.septo}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'septo')}
                  />
                </Col>
                <Col>
                  <Form.Label>PP VE</Form.Label>
                  <Form.Control
                    name="ppve"
                    style={editStyle}
                    type="number"
                    size="sm"
                    value={formData.ppve}
                    ref={ecoRefs.current['ppve']}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'ppve')}
                  />
                </Col>
                <Col>
                  <Form.Label>DD (cm)</Form.Label>
                  <Form.Control
                    name="dd"
                    style={editStyle}
                    type="number"
                    size="sm"
                    value={formData.dd}
                    ref={ecoRefs.current['dd']}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'dd')}
                  />
                </Col>
                <Col>
                  <Form.Label>DS (cm)</Form.Label>
                  <Form.Control
                    name="ds"
                    style={editStyle}
                    type="number"
                    size="sm"
                    value={formData.ds}
                    ref={ecoRefs.current['ds']}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'ds')}
                  />
                </Col>
                <Col>
                  <Form.Label>Fraç Ej (Biplanar)</Form.Label>
                  <Form.Control
                    name="fraejeBiplanar"
                    style={editStyle}
                    type="number"
                    size="sm"
                    value={formData.fraejeBiplanar}
                    ref={ecoRefs.current['fraejeBiplanar']}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'fraejeBiplanar')}
                  />
                </Col>
                <Col>
                  <Form.Label>FE (3D)</Form.Label>
                  <Form.Control
                    name="fe3d"
                    style={editStyle}
                    type="number"
                    size="sm"
                    // ref={ecoRefs.current[0]}
                    value={formData.fe3d}
                    onChange={handleChange}
                  />
                </Col>
              </Row>
            </Form.Group>
            <Form.Group>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  <Form.Label>Achados Descritivos</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.achadosDescritivos.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.achadosDescritivos?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'achadosDescritivos', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'achadosDescritivos', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="achadosDescritivos"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.achados?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODACHAVE} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  <Form.Label>Contração Segmentar</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.contracaoSegmentar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.contracaoSegmentar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'contracaoSegmentar', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'contracaoSegmentar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="contracaoSegmentar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.contraRegiao?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONTRASEG} —{' '}
                      {o.CONTRACAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  <Form.Label>Região</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.regiaoVE.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.regiaoVE?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'regiaoVE', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'regiaoVE', i)}
                      />
                    ))}
                    <DeleteButton
                      field="regiaoVE"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.contraRegiao?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREGIAO} — {o.REGIAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}
                </Col>
              </Row>
            </Form.Group>

            <h5>III - Avaliação da Função Diastólica do VE</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Tempo Ejeção VE</Form.Label>
                    <Form.Control
                      name="tempoEjecaoVE"
                      style={editStyle}
                      type="number"
                      ref={ecoRefs.current['tempoEjecaoVE']}
                      value={formData.tempoEjecaoVE}
                      onChange={handleChange}
                      onKeyDown={e => handleEcoKeyDown(e, 'tempoEjecaoVE')}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Tempo AE Mitral</Form.Label>
                    <Form.Control
                      name="tempoAEMitral"
                      style={editStyle}
                      type="number"
                      ref={ecoRefs.current['tempoAEMitral']}
                      value={formData.tempoAEMitral}
                      onChange={handleChange}
                      onKeyDown={e => handleEcoKeyDown(e, 'tempoAEMitral')}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Índice Cardíaco</Form.Label>
                    <Form.Control
                      name="indiceCardiaco"
                      style={editStyle}
                      type="number"
                      ref={ecoRefs.current['indiceCardiaco']}
                      value={formData.indiceCardiaco}
                      onChange={handleChange}
                      onKeyDown={e => handleEcoKeyDown(e, 'indiceCardiaco')}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Comentários da Função Diastólica</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.comentariosDiast.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.comentariosDiast?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'comentariosDiast', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'comentariosDiast', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="comentariosDiast"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.avDiast?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODAVDIAST} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>
            </Form.Group>

            {/* IV - Valva Mitral e Átrio Esquerdo */}
            <h5>IV - Valva Mitral e Átrio Esquerdo</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Diâmetro do AE (cm)</Form.Label>
                  <Form.Control
                    name="diamAE"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['diamAE']}
                    value={formData.diamAE}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'diamAE')}
                  />
                </Col>
                <Col>
                  <Form.Label>Índice Vol. AE</Form.Label>
                  <Form.Control
                    name="indiceVolAE"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['indiceVolAE']}
                    value={formData.indiceVolAE}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'indiceVolAE')}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Átrio Esquerdo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.atrioEsq.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.atrioEsq?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'atrioEsq', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'atrioEsq', i)}
                      />
                    ))}
                    <DeleteButton
                      field="atrioEsq"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.atrioEsq?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODAE} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Valva Mitral</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvaMitral.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvaMitral?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'valvaMitral', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvaMitral', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvaMitral"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvaMitral?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVM} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            {/* V - Valva Aórtica e Aorta */}
            <h5>V - Valva Aórtica e Aorta</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Diâm. Valv. AO (mm)</Form.Label>
                  <Form.Control
                    name="diamAO"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['diamAO']}
                    value={formData.diamAO}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'diamAO')}
                  />
                </Col>
                <Col>
                  <Form.Label>Diâmetro da Via de Saída do VE (cm)</Form.Label>
                  <Form.Control
                    name="diamSaidaVE"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['diamSaidaVE']}
                    value={formData.diamSaidaVE}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'diamSaidaVE')}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Valva Aórtica</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvaAortica.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvaAortica?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'valvaAortica', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvaAortica', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvaAortica"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvaAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVAO} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Aorta</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.aorta.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.aorta?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'aorta', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'aorta', i)}
                      />
                    ))}
                    <DeleteButton
                      field="aorta"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.aorta?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODAORTA} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            {/* VI - Cavidades Direita e Valvas */}
            <h5>VI - Cavidades Direita e Valvas</h5>
            <Form.Group>
              <Row>
                <Col xs="auto">
                  <Form.Label>Átrio Direito</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.atrioDireito.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.atrioDireito?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'atrioDireito', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'atrioDireito', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="atrioDireito"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.atrioDir?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODAD} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>

                <Col xs="auto">
                  <Form.Label>Veia Cava Inferior</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.veiaCavaInferior.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.veiaCavaInferior?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'veiaCavaInferior', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'veiaCavaInferior', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="veiaCavaInferior"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.veiaCavaInf?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVCINF} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>

              <Row>
                <Col xs="auto">
                  <Form.Label>Ventrículo Direito</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.ventriculoDireito.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.ventriculoDireito?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'ventriculoDireito', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'ventriculoDireito', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="ventriculoDireito"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.ventriDir?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVD} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>{' '}
                <Col xs="auto">
                  <Form.Label>Valva Tricúspide</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvaTricuspide.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvaTricuspide?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'valvaTricuspide', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvaTricuspide', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvaTricuspide"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvaTricu?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVT} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>Diâm. do Anel Valv. Pulmonar (cm)</Form.Label>
                  <Form.Control
                    style={editStyle}
                    type="number"
                    name="diamAnelPulmonar"
                    // ref={ecoRefs.current[0]}
                    value={formData.diamAnelPulmonar}
                    onChange={handleChange}
                  // onKeyDown={e => handleEcoKeyDown(e, 0)}
                  />
                </Col>{' '}
                <Col xs="auto">
                  <Form.Label>Valva Pulmonar</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvaPulmonar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvaPulmonar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'valvaPulmonar', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvaPulmonar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvaPulmonar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvaPulmo?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVP} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            <h5>VII - Pericárdio</h5>
            <Form.Group>
              <Row>
                <Col xs="auto">
                  <div className="d-flex flex-wrap gap-1">
                    {formData.pericardio.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.pericardio?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'pericardio', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'pericardio', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="pericardio"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.pericardio?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODPERICARDIO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            <h5>VIII - Valvulopatia da Valva Mitral</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Área (cm²)</Form.Label>
                  <Form.Control
                    name="areaMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['areaMitral']}
                    value={formData.areaMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'areaMitral')}
                  />
                </Col>
                <Col>
                  <Form.Label>Gradiente Médio (mm Hg)</Form.Label>
                  <Form.Control
                    name="gradienteMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradienteMitral']}
                    value={formData.gradienteMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradienteMitral')}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoMitral.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoMitral?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'refluxoMitral', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoMitral', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoMitral"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refVM?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFVM} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>Pisa</Form.Label>
                  <Form.Control
                    name="pisaMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['pisaMitral']}
                    value={formData.pisaMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'pisaMitral')}
                  />
                </Col>
                <Col>
                  <Form.Label>Volume Regurgitação</Form.Label>
                  <Form.Control
                    name="volumeRegMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['volumeRegMitral']}
                    value={formData.volumeRegMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'volumeRegMitral')}
                  />
                </Col>
              </Row>
            </Form.Group>

            {/* IX - Valvulopatia da Valva Aórtica */}
            <h5>IX - Valvulopatia da Valva Aórtica</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Área (cm²)</Form.Label>
                  <Form.Control
                    name="areaAortica"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['areaAortica']}
                    value={formData.areaAortica}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'areaAortica')}
                  />
                </Col>
                <Col>
                  <Form.Label>
                    Pisa (*Não está sendo usado mais no Delphi*)
                  </Form.Label>
                  <Form.Control
                    name="pisaAortica"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['pisaAortica']}
                    value={formData.pisaAortica}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'pisaAortica')}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoAortica.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoAortica?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoAortica', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoAortica', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoAortica"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refVAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFVAO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>GMVAO (mm Hg)</Form.Label>
                  <Form.Control
                    name="gmvao"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gmvao']}
                    value={formData.gmvao}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gmvao')}
                  />
                </Col>
                <Col>
                  <Form.Label>GMVSVE (mm Hg)</Form.Label>
                  <Form.Control
                    name="gmvsve"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gmvsve']}
                    value={formData.gmvsve}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gmvsve')}
                  />
                </Col>
              </Row>
            </Form.Group>
            <hr />
            {/* X - Valvulopatia de Valva Tricúspide/Pulmonar */}
            <h5>X - Valvulopatia de Valva Tricúspide/Pulmonar</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>GMV Tric (mm Hg)</Form.Label>
                  <Form.Control
                    name="gmvTric"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gmvTric']}
                    value={formData.gmvTric}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gmvTric')}
                  />
                </Col>
                <Col>
                  <Form.Label>GPIT (mm Hg)</Form.Label>
                  <Form.Control
                    name="gpit"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gpit']}
                    value={formData.gpit}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gpit')}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label>Refluxo (Tric)</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoTric.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoTric?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'refluxoTric', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoTric', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoTric"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refVT?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFVT} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>GMV Pulm (mm Hg)</Form.Label>
                  <Form.Control
                    name="gradientePulm"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradientePulm']}
                    value={formData.gradientePulm}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradientePulm')}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label>Refluxo (Pulm)</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoPulm.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoPulm?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'refluxoPulm', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoPulm', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoPulm"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refVP?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFVP} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            <h5>XI - Próteses</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Prótese Mitral</Form.Label>
                  <Form.Control
                    name="gradMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradMitral']}
                    value={formData.gradMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradMitral')}
                  />{' '}
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoProtMitral.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoProtMitral?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoProtMitral', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoProtMitral', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoProtMitral"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPM?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFPM} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeProtMitral.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeProtMitral?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeProtMitral', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeProtMitral', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeProtMitral"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPM?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col>
                  <Form.Label>Prótese Aórtica</Form.Label>
                  <Form.Control
                    name="gradAortica"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradAortica']}
                    value={formData.gradAortica}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradAortica')}
                  />{' '}
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoProtAortica.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoProtAortica?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoProtAortica', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoProtAortica', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoProtAortica"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFPAO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeProtAortica.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeProtAortica?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeProtAortica', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeProtAortica', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeProtAortica"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col>
                  <Form.Label>Prótese Tricúspide</Form.Label>
                  <Form.Control
                    name="gradTricuspide"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradTricuspide']}
                    value={formData.gradTricuspide}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradTricuspide')}
                  />{' '}
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoProtTricuspide.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoProtTricuspide?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoProtTricuspide', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoProtTricuspide', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoProtTricuspide"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPT?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFPT} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeProtTricuspide.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={
                          ecoArrayRefs.current.intensidadeProtTricuspide?.[i]
                        }
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeProtTricuspide', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(
                            e,
                            'intensidadeProtTricuspide',
                            i
                          )
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeProtTricuspide"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPT?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>

                <Col>
                  <Form.Label>Prótese Pulmonar</Form.Label>
                  <Form.Control
                    name="gradPulmonar"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradPulmonar']}
                    value={formData.gradPulmonar}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradPulmonar')}
                  />{' '}
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoProtPulmonar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoProtPulmonar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoProtPulmonar', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoProtPulmonar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoProtPulmonar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPP?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFPP} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeProtPulmonar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeProtPulmonar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeProtPulmonar', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeProtPulmonar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeProtPulmonar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPP?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            {/* XII - Conclusões */}
            <Form.Group>
              <Form.Label>XII - Conclusões</Form.Label>
              <Row>
                <Col xs="auto">
                  <Form.Label>Valvulopatia</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvulopatia.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvulopatia?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'valvulopatia', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvulopatia', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvulopatia"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvulopatia?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVALVO} — {o.VALVO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Prótese</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.protese.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.protese?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'protese', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'protese', i)}
                      />
                    ))}
                    <DeleteButton
                      field="protese"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.protese?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODPROTESE} —{' '}
                      {o.PROTESE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Con Seg.</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.conSeg.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.conSeg?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'conSeg', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'conSeg', i)}
                      />
                    ))}
                    <DeleteButton
                      field="conSeg"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.contraSeg?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONTRASEG} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeValv.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeValv?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeValv', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeValv', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeValv"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvulopatia?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>

                <Col xs="auto">
                  <Form.Label>Desc.</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.descProtese.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.descProtese?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'descProtese', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'descProtese', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="descProtese"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.protese?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CDODESCRICAO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Região</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.regiao.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.regiao?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'regiao', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'regiao', i)}
                      />
                    ))}
                    <DeleteButton
                      field="regiao"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.contraSeg?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREGIAO} — {o.REGIAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Predominância</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.predominanciaValv.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.predominanciaValv?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'predominanciaValv', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'predominanciaValv', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="predominanciaValv"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvulopatia?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODPREDOMI} —{' '}
                      {o.PREDOMI || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Predominância</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.predominanciaProtese.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.predominanciaProtese?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'predominanciaProtese', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'predominanciaProtese', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="predominanciaProtese"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.protese?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODPREDOMI} —{' '}
                      {o.PREDOMI || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col></Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Conclusões</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.conclusoes.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.conclusoes?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'conclusoes', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'conclusoes', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="conclusoes"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.conclusoes?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONCLU} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeConclusoes.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeConclusoes?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeConclusoes', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeConclusoes', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeConclusoes"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.conclusoes?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>
            <Form.Group size="sm" controlId="editDescricao">
              <strong>OBSERVAÇÕES</strong>
              <Button
                variant="outline-info"
                size="sm"
                className="mb-2 ml-2"
                onClick={() => setShowModal(true)}
              >
                Buscar Observação Anterior
              </Button>
              <Form.Control
                name="observacoes"
                rows="5"
                value={formData.observacoes || ''}
                onChange={handleChange}
                size="sm"
                ref={ecoRefs.current['observacoes']}
                onKeyDown={e => handleEcoKeyDown(e, 'observacoes')}
                as="textarea"
              />
            </Form.Group>

            <ModalEco
              show={showModal}
              onHide={() => setShowModal(false)}
              codPac={eco?.CODPAC}
              onSelect={obs => {
                setFormData(prev => ({ ...prev, observacoes: obs }));
                setShowModal(false);
              }}
              onCopy={ecoSelected => {
                setFormData(prev => ({
                  ...prev,
                  ...mapEcoFormFields(ecoSelected),
                  superficiecorporea: superficieCorporea || eco?.SUPCORP,
                }));
                setShowModal(false);
              }}
            />

            <Card.Footer className="text-center">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{' '}
                    Salvando...
                  </>
                ) : (
                  'Gravar'
                )}
              </Button>{' '}
              <Button
                variant="secondary"
                onClick={() => {
                  history.push({
                    pathname: `/reportDoppler/${eco?.CODECO}`,
                    state: { printing: false },
                  });
                }}
              >
                Imprimir
              </Button>{' '}
              <Button
                variant="secondary"
                onClick={() => {
                  history.push({
                    pathname: `/reportDoppler/${eco?.CODECO}`,
                    state: { printing: true },
                  });
                }}
              >
                Imprimir Timbrado
              </Button>{' '}
              <Button
                variant="secondary"
                onClick={() => generateDopplerPDF(eco, 'imprimirTimbrado')}
              >
                Visualizar PDF
              </Button>{' '}
              <Button
                variant="outline-success"
                disabled={isSubmitting}
                onClick={() => generateDopplerPDF(eco, 'envioWpp')}
              >
                <FaWhatsapp /> Enviar PDF
              </Button>{' '}
              <Button variant="warning" onClick={() => setShowUpload(true)}>
                Anexar
              </Button>{' '}
              <Button
                variant="warning"
                disabled
                onClick={() => generateDopplerTextReport(eco)}
              >
                Gerar TXT
              </Button>
            </Card.Footer>
          </Form>
        </Card.Body>
      </Card>

      <Modal show={showUpload} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={true}
            onChange={e => setFiles(Array.from(e.target.files))}
          />
          <ul>
            {files && files.map(({ name }) => <li key={name}>{name}</li>)}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          {isUploading ? (
            <Spinner animation="border" variant="dark" />
          ) : (
            <Button onClick={confirmUpload}>Confirmar</Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
